import { sankey, sankeyLinkHorizontal, type SankeyLink, type SankeyNode } from "d3-sankey";
import { useMemo, useState } from "react";
import { edgeTitle, readableLabel } from "../lib/journeys";
import type { JourneyEdge } from "@starsach/shared-types";

interface NodeDatum { id: string; label: string }
interface LinkDatum { source: string; target: string; value: number; edge: JourneyEdge }
type N = SankeyNode<NodeDatum, LinkDatum>;
type L = SankeyLink<NodeDatum, LinkDatum>;

/**
 * Company-level path view (ADR-0003 Track A). Nodes are keyed on the
 * edge ids the aggregator produced (flow + block id), so a renamed block
 * does not split the diagram. d3's sankey needs an acyclic graph, so a
 * transition back to an earlier step is drawn into a "Back to <step>"
 * terminal rather than left out (ADR-0012 phase 1); retries folded by
 * toSteps() arrive as a count and are written on the step.
 */
export function Sankey({
  edges,
  retries,
  width = 960,
  height = 520,
  onSelectEdge,
  selected,
}: {
  edges: JourneyEdge[];
  /** Retries folded into a step (ADR-0012): node id → contacts that came back to it. Shown on the node. */
  retries?: Map<string, number>;
  width?: number;
  height?: number;
  onSelectEdge?: (e: JourneyEdge) => void;
  selected?: JourneyEdge | null;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const graph = useMemo(() => {
    const ids = new Map<string, NodeDatum>();
    for (const e of edges) {
      ids.set(e.from, { id: e.from, label: readableLabel(e.fromLabel, e.from) });
      ids.set(e.to, { id: e.to, label: readableLabel(e.toLabel, e.to) });
    }
    // A back-edge - a deliberate return to an earlier step - is drawn as its own terminal, "Back to <step>", so the
    // layout stays acyclic and nothing is left out of the picture.
    const order = topoRank(edges);
    const links: LinkDatum[] = edges.map((e) => {
      const back = (order.get(e.from) ?? 0) >= (order.get(e.to) ?? 0);
      if (!back) return { source: e.from, target: e.to, value: Math.max(e.count, 1), edge: e };
      const id = `back:${e.to}`;
      ids.set(id, { id, label: `Back to ${readableLabel(e.toLabel, e.to)}` });
      return { source: e.from, target: id, value: Math.max(e.count, 1), edge: e };
    });
    const nodes = [...ids.values()].map((n) => {
      const r = retries?.get(n.id);
      return r ? { ...n, label: `${n.label} (${r} retried)` } : n;
    });
    if (nodes.length === 0 || links.length === 0) return { nodes: [] as N[], links: [] as L[] };
    const layout = sankey<NodeDatum, LinkDatum>()
      .nodeId((d) => d.id)
      .nodeWidth(14)
      .nodePadding(14)
      .extent([[1, 1], [width - 1, height - 6]]);
    const g = layout({ nodes: nodes.map((n) => ({ ...n })), links: links.map((l) => ({ ...l })) });
    return { nodes: g.nodes, links: g.links };
  }, [edges, retries, width, height]);

  if (graph.nodes.length === 0) return <p className="text-sm text-neutral-600 dark:text-neutral-400">No paths to draw.</p>;
  const max = Math.max(...graph.links.map((l) => l.value));
  const linkKey = (l: L) => `${(l.source as N).id}->${(l.target as N).id}#${l.edge.branch ?? ""}`;
  const isSelected = (l: L) => selected && l.edge.from === selected.from && l.edge.to === selected.to;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full min-w-[640px]" role="group" aria-label={`Contact flow paths: ${graph.nodes.length} blocks, ${graph.links.length} transitions, busiest ${max} contacts`}>
        <g>
          {graph.links.map((l) => {
            const k = linkKey(l);
            const errorRate = l.edge.count ? l.edge.errorCount / l.edge.count : 0;
            const active = hover === k || isSelected(l);
            return (
              <path
                key={k}
                d={sankeyLinkHorizontal()(l) ?? undefined}
                fill="none"
                stroke={errorRate > 0.05 ? "#b91c1c" : "var(--color-accent)"}
                strokeOpacity={active ? 0.75 : 0.28}
                strokeWidth={Math.max(1, l.width ?? 1)}
                className={onSelectEdge ? "cursor-pointer" : undefined}
                onMouseEnter={() => setHover(k)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelectEdge?.(l.edge)}
                role={onSelectEdge ? "button" : undefined}
                tabIndex={onSelectEdge ? 0 : undefined}
                onKeyDown={(ev) => { if (onSelectEdge && (ev.key === "Enter" || ev.key === " ")) { ev.preventDefault(); onSelectEdge(l.edge); } }}
                aria-label={`${edgeTitle(l.edge)}: ${l.edge.count} contacts${l.edge.errorCount ? `, ${l.edge.errorCount} errors` : ""}`}
              >
                <title>{`${edgeTitle(l.edge)}: ${l.edge.count} contacts${l.edge.avgDwellMs ? `, avg ${(l.edge.avgDwellMs / 1000).toFixed(1)}s` : ""}${l.edge.errorCount ? `, ${l.edge.errorCount} errors` : ""}`}</title>
              </path>
            );
          })}
        </g>
        <g>
          {graph.nodes.map((n) => (
            <g key={n.id} transform={`translate(${n.x0 ?? 0},${n.y0 ?? 0})`}>
              <rect width={(n.x1 ?? 0) - (n.x0 ?? 0)} height={Math.max(1, (n.y1 ?? 0) - (n.y0 ?? 0))} fill="currentColor" opacity={0.75} rx={2}>
                <title>{`${n.label}: ${n.value ?? 0} contacts`}</title>
              </rect>
              <text x={(n.x0 ?? 0) < width / 2 ? (n.x1 ?? 0) - (n.x0 ?? 0) + 6 : -6} y={((n.y1 ?? 0) - (n.y0 ?? 0)) / 2} dy="0.35em" textAnchor={(n.x0 ?? 0) < width / 2 ? "start" : "end"} fontSize={11} fill="currentColor">
                {n.label}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}

/** Longest-path rank from the sources; back-edges are the ones that go against it. */
function topoRank(edges: JourneyEdge[]): Map<string, number> {
  const out = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const e of edges) {
    out.set(e.from, [...(out.get(e.from) ?? []), e.to]);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
    indeg.set(e.from, indeg.get(e.from) ?? 0);
  }
  const rank = new Map<string, number>();
  const queue = [...indeg].filter(([, d]) => d === 0).map(([n]) => n);
  for (const n of queue) rank.set(n, 0);
  while (queue.length) {
    const n = queue.shift()!;
    for (const m of out.get(n) ?? []) {
      rank.set(m, Math.max(rank.get(m) ?? 0, (rank.get(n) ?? 0) + 1));
      indeg.set(m, (indeg.get(m) ?? 1) - 1);
      if (indeg.get(m) === 0) queue.push(m);
    }
  }
  // Nodes inside cycles never reach indegree 0; give them a rank after everything reachable.
  let next = Math.max(0, ...rank.values()) + 1;
  for (const n of indeg.keys()) if (!rank.has(n)) rank.set(n, next++);
  return rank;
}
